import React, { useMemo } from 'react';
import DashboardPieChart from './charts/DashboardPieChart';
import DashboardDonutChart from './charts/DashboardDonutChart';
import DashboardBarChart from './charts/DashboardBarChart';
import {
    PATIENT_AGE_GROUP_STATS,
    PATIENT_COMMUNICATION_STATS,
    PATIENT_STATUS_STATS,
} from '../dashboardStatsConfig';
import { parseStatCount } from '../dashboardStatUtils';

const buildSeries = (config, getStat) =>
    config.map((item) => ({
        label: item.label,
        color: item.color,
        value: parseStatCount(getStat(item.key)),
    }));

const hasValues = (series) => series.some((item) => item.value > 0);

const CHART_COMPONENTS = {
    pie: DashboardPieChart,
    donut: DashboardDonutChart,
    bar: DashboardBarChart,
};

function PatientStatChart({ title, icon, series, type, chartProps = {} }) {
    const total = series.reduce((sum, item) => sum + item.value, 0);
    const Chart = CHART_COMPONENTS[type] || DashboardPieChart;

    return (
        <div className="dashboard-patient-chart">
            <div className="dashboard-patient-chart__header">
                <span className="dashboard-patient-chart__icon" aria-hidden="true">
                    <i className={`bi ${icon}`} />
                </span>
                <div>
                    <h3 className="dashboard-patient-chart__title mb-0">{title}</h3>
                    <p className="dashboard-patient-chart__subtitle mb-0">
                        Total: <strong>{total.toLocaleString()}</strong>
                    </p>
                </div>
            </div>
            {hasValues(series) ? (
                <Chart series={series} title={title} {...chartProps} />
            ) : (
                <div className="dashboard-empty-state">
                    <i className={`bi ${type === 'bar' ? 'bi-bar-chart' : 'bi-pie-chart'} me-2`} />
                    No data available
                </div>
            )}
        </div>
    );
}

function DashboardPatientStats({ getStat, isLoading, loadError, lastUpdated }) {
    const statusSeries = useMemo(() => buildSeries(PATIENT_STATUS_STATS, getStat), [getStat]);
    const ageGroupSeries = useMemo(() => buildSeries(PATIENT_AGE_GROUP_STATS, getStat), [getStat]);
    const communicationSeries = useMemo(() => buildSeries(PATIENT_COMMUNICATION_STATS, getStat), [getStat]);

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--indigo">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-bar-chart-fill" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">Patient Stats</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">
                                {lastUpdated
                                    ? `Status, age, and communication breakdown · Updated ${lastUpdated}`
                                    : 'Patient status, age group, and communication breakdown'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="row g-3">
                        {[0, 1, 2].map((index) => (
                            <div key={index} className="col-12 col-xl-4">
                                <div className="dashboard-stat-tile dashboard-stat-tile--skeleton" style={{ minHeight: 280 }} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="row g-3 dashboard-patient-stats-grid">
                        <div className="col-12 col-xl-4">
                            <PatientStatChart
                                title="Patient Status"
                                icon="bi-person-check"
                                series={statusSeries}
                                type="pie"
                            />
                        </div>
                        <div className="col-12 col-xl-4">
                            <PatientStatChart
                                title="Age Groups"
                                icon="bi-people"
                                series={ageGroupSeries}
                                type="bar"
                            />
                        </div>
                        <div className="col-12 col-xl-4">
                            <PatientStatChart
                                title="Communication"
                                icon="bi-broadcast-pin"
                                series={communicationSeries}
                                type="bar"
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default DashboardPatientStats;
